import z from "zod";

export const customerPriceLevelTabSchema = z.object({
  priceLevelId: z.union([z.string().uuid(), z.literal("None"), z.literal("")]),
  accountId: z.string().uuid(),
});

export const customerDrawSetupTabSchema = z.object({
  draws: z
    .array(
      z.object({
        name: z.string().min(1, "Name is required"),
        percentage: z.preprocess(
          (value) =>
            typeof value === "number" && Number.isNaN(value)
              ? undefined
              : value,
          z
            .number({
              required_error: "Percentage is required",
              invalid_type_error: "Percentage is required",
            })
            .max(100, "Percentage must be ≤ 100%")
        ),
      })
    )
    .superRefine((items, ctx) => {
      const uniqueValues = new Map();
      items.forEach((item, index) => {
        const firstIndex = uniqueValues.get(item.name);
        if (firstIndex !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Draw name must be unique`,
            path: [index, "name"],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Draw name must be unique`,
            path: [firstIndex, "name"],
          });
          return;
        }
        uniqueValues.set(item.name, index);
      });
    }),
  drawTotal: z.number().refine((val) => val === 100, {
    message: "Total must equal 100%",
  }),
});

const dropDrawSchema = z.object({
  id: z.string().optional(),
  createdOn: z.string().optional(),
  modifiedOn: z.string().optional(),
  accountId: z.string().optional(),
  accountDrawId: z.string(),
  percentage: z.preprocess(
    (value) =>
      typeof value === "number" && Number.isNaN(value) ? undefined : value,
    z
      .number({
        required_error: "Percentage is required",
        invalid_type_error: "Percentage is required",
      })
      .max(100, "Percentage must be ≤ 100%")
  ),
});

const categoryDrawSchema = z.object({
  id: z.string().optional(),
  createdOn: z.string().optional(),
  modifiedOn: z.string().optional(),
  name: z.string().optional(),
  displayOrder: z.number().optional(),
  categoryId: z.string(),
  accountDrawId: z.string(),
  percentage: z.preprocess(
    (value) =>
      typeof value === "number" && Number.isNaN(value) ? undefined : value,
    z
      .number({
        required_error: "Percentage is required",
        invalid_type_error: "Percentage is required",
      })
      .max(100, "Percentage must be ≤ 100%")
  ),
});

const categoryPricingDetailsSchema = z.object({
  id: z.string().optional(),
  createdOn: z.string().optional(),
  modifiedOn: z.string().optional(),
  categoryId: z.string(),
  categoryName: z.string(),
  accountId: z.string(),
  drawTotal: z.number().refine((val) => val === 100, {
    message: "Total must equal 100%",
  }),
  draws: z.array(categoryDrawSchema),
});

export const customerDrawsDistributionTabSchema = z.object({
  dropDistribution: z
    .object({
      draws: z.array(dropDrawSchema),
      dropTotal: z.number().refine((val) => val === 100, {
        message: "Total must equal 100%",
      }),
    })
    .optional(),
  categoryDistributions: z.array(categoryPricingDetailsSchema),
});

export const customerLineItemsTabsSchema = z.object({
  accountId: z.string().uuid(),
  name: z.string().min(1, "Name is required"),
  footnote: z.string().optional(),
  categoryIds: z
    .array(z.string().uuid())
    .min(1, "At least one category must be selected"),
  bidLineItemType: z.enum(["Bid", "Option"]),
});
